import React, { useEffect, useState } from "react";
import { Row, Col, Modal, Form, Input } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import configure from "configure";
import Loader from "pages/Separate/Loader";

// ─── Constants ─────────────────────────────────────────────────────────────────
const PROVIDER_OPTIONS = ["Anthropic", "OpenAI", "Google", "Other"];

const EMPTY_FORM = {
  description: "",
  provider_name: "Anthropic",
  model_name: "",
  api_key: "",
  is_active: false,
};

// ─── Small UI atoms (same look & feel as EmailConfigIndex) ─────────────────────
const Badge = ({ children, bg, color }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 600,
      padding: "3px 9px",
      borderRadius: 20,
      background: bg,
      color,
      letterSpacing: ".02em",
      lineHeight: 1,
    }}
  >
    {children}
  </span>
);

const FieldLabel = ({ children, required }) => (
  <label
    style={{
      fontSize: 12,
      fontWeight: 600,
      color: "#4b5563",
      marginBottom: 5,
      display: "block",
      letterSpacing: ".01em",
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span>}
  </label>
);

const FieldHint = ({ children, color }) => (
  <p
    style={{
      fontSize: 11,
      color: color || "#9ca3af",
      marginTop: 4,
      marginBottom: 0,
      lineHeight: 1.4,
    }}
  >
    {children}
  </p>
);

const Divider = ({ label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      margin: "20px 0 16px",
    }}
  >
    <div style={{ flex: 1, height: 1, background: "#f0f0f0" }} />
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: "#c4b5c9",
        letterSpacing: ".1em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
    <div style={{ flex: 1, height: 1, background: "#f0f0f0" }} />
  </div>
);

// ─── Toggle Switch ─────────────────────────────────────────────────────────────
const Toggle = ({ id, checked, onChange, label, hint }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 8,
      background: checked ? "#fdf4fb" : "#f9fafb",
      border: `1px solid ${checked ? "#e8bedd" : "#e5e7eb"}`,
      cursor: "pointer",
      transition: "all .15s",
    }}
    onClick={() => onChange(!checked)}
  >
    <div
      className="form-check form-switch mb-0"
      style={{ paddingLeft: 0, minWidth: 40 }}
    >
      <input
        className="form-check-input"
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        style={{ cursor: "pointer", marginLeft: 0, width: 36, height: 20 }}
      />
    </div>
    <div>
      <p
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: "#374151",
          margin: 0,
          lineHeight: 1.3,
        }}
      >
        {label}
      </p>
      {hint && (
        <p
          style={{
            fontSize: 11,
            color: "#9ca3af",
            margin: "2px 0 0",
            lineHeight: 1.3,
          }}
        >
          {hint}
        </p>
      )}
    </div>
  </div>
);

const providerColors = {
  Anthropic: { bg: "#f5e8f1", color: "#981f77" },
  OpenAI: { bg: "#dcfce7", color: "#166534" },
  Google: { bg: "#dbeafe", color: "#1d4ed8" },
  Other: { bg: "#f1f5f9", color: "#475569" },
};

// ─── Credential Card ───────────────────────────────────────────────────────────
const CredentialCard = ({ cred, onEdit, onDelete, onSetActive }) => {
  const pc = providerColors[cred.provider_name] || providerColors.Other;
  return (
    <div
      style={{
        border: cred.is_active ? "1.5px solid #c97ab8" : "1px solid #e9ecef",
        borderLeft: `4px solid ${cred.is_active ? "#981f77" : "#e9ecef"}`,
        borderRadius: 10,
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: cred.is_active ? "#fefbfe" : "#fff",
        transition: "box-shadow .15s, border-color .15s",
        gap: 16,
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,.06)")
      }
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
    >
      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 5,
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>
            {cred.description}
          </span>
          {cred.is_active && (
            <Badge bg="#f5e8f1" color="#981f77">
              ⭐ Active
            </Badge>
          )}
          <Badge bg={pc.bg} color={pc.color}>
            {cred.provider_name}
          </Badge>
          <Badge
            bg={cred.is_active ? "#dcfce7" : "#f1f5f9"}
            color={cred.is_active ? "#166534" : "#6b7280"}
          >
            {cred.is_active ? "● Active" : "○ Inactive"}
          </Badge>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            fontSize: 12,
            color: "#6b7280",
            flexWrap: "wrap",
          }}
        >
          <span title="Model">
            <i className="fas fa-microchip me-1" style={{ color: "#c4b5c9" }}></i>
            {cred.model_name}
          </span>
          <span title="API Key">
            <i className="fas fa-key me-1" style={{ color: "#c4b5c9" }}></i>
            <span style={{ fontFamily: "monospace" }}>{cred.api_key}</span>
          </span>
          {cred.token_usage !== undefined && (
            <span title="Token usage">
              <i className="fas fa-bolt me-1" style={{ color: "#c4b5c9" }}></i>
              {cred.token_usage} tokens used
            </span>
          )}
          {cred.created_at && (
            <span title="Added">
              <i className="fas fa-clock me-1" style={{ color: "#c4b5c9" }}></i>
              {cred.created_at}
            </span>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <ActionBtn
          icon="fa-star"
          title={cred.is_active ? "Already active" : "Set as active"}
          onClick={onSetActive}
          active={cred.is_active}
          activeColor="#981f77"
        />
        <ActionBtn icon="fa-pencil-alt" title="Edit" onClick={onEdit} />
        <ActionBtn
          icon="fa-trash-alt"
          title={cred.is_active ? "Can't delete the active key" : "Delete"}
          onClick={onDelete}
          danger
          disabled={cred.is_active}
        />
      </div>
    </div>
  );
};

const ActionBtn = ({ icon, title, onClick, active, activeColor, danger, disabled }) => (
  <button
    onClick={disabled ? undefined : onClick}
    title={title}
    disabled={disabled}
    style={{
      width: 32,
      height: 32,
      borderRadius: 7,
      border: `1px solid ${
        active ? activeColor : danger ? "#fca5a5" : "#e5e7eb"
      }`,
      background: active ? "#fdf4fb" : "transparent",
      color: disabled
        ? "#e5e7eb"
        : active
        ? activeColor
        : danger
        ? "#ef4444"
        : "#9ca3af",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "all .15s",
      padding: 0,
      opacity: disabled ? 0.6 : 1,
    }}
    onMouseEnter={(e) => {
      if (disabled) return;
      e.currentTarget.style.background = active
        ? "#f5e8f1"
        : danger
        ? "#fee2e2"
        : "#f9fafb";
      e.currentTarget.style.color = active
        ? activeColor
        : danger
        ? "#dc2626"
        : "#374151";
      e.currentTarget.style.borderColor = active
        ? activeColor
        : danger
        ? "#f87171"
        : "#d1d5db";
    }}
    onMouseLeave={(e) => {
      if (disabled) return;
      e.currentTarget.style.background = active ? "#fdf4fb" : "transparent";
      e.currentTarget.style.color = active
        ? activeColor
        : danger
        ? "#ef4444"
        : "#9ca3af";
      e.currentTarget.style.borderColor = active
        ? activeColor
        : danger
        ? "#fca5a5"
        : "#e5e7eb";
    }}
  >
    <i className={`fas ${icon}`} style={{ fontSize: 11 }}></i>
  </button>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const ManageAiKeyIndex = () => {
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showKey, setShowKey] = useState(false);

  // OpenRouter model list, grouped by provider
  const [groupedModels, setGroupedModels] = useState({
    Anthropic: [],
    OpenAI: [],
    Google: [],
  });
  const [loadingModels, setLoadingModels] = useState(false);

  // ── Fetch credentials ───────────────────────────────────────────────────────
  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${configure.nodeApi}/aiCredentials`, {
        headers: { Accept: "application/json" },
      });
      if (res.data.success) {
        const parsed = res.data.data.map((c) => ({
          ...c,
          created_at: c.created_at
            ? new Date(c.created_at).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : null,
        }));
        setCredentials(parsed);
      }
    } catch (err) {
      console.error("fetchCredentials error:", err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch model list (for the model dropdown) ──────────────────────────────
  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const res = await axios.get(`${configure.nodeApi}/aiCredentials/models`, {
        headers: { Accept: "application/json" },
      });
      if (res.data.success) {
        setGroupedModels({
          Anthropic: res.data.grouped?.Anthropic || [],
          OpenAI: res.data.grouped?.OpenAI || [],
          Google: res.data.grouped?.Google || [],
        });
      }
    } catch (err) {
      console.warn("Could not fetch model list:", err.message);
    } finally {
      setLoadingModels(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, []);

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openAddModal = () => {
    setIsEditing(false);
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowKey(false);
    setModalOpen(true);
    fetchModels();
  };

  const openEditModal = (cred) => {
    setIsEditing(true);
    setEditId(cred.id);
    setForm({
      description: cred.description || "",
      provider_name: cred.provider_name || "Anthropic",
      model_name: cred.model_name || "",
      api_key: "",
      is_active: !!cred.is_active,
    });
    setShowKey(false);
    setModalOpen(true);
    fetchModels();
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(EMPTY_FORM);
  };

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // When provider changes, clear model_name unless it's "Other" (free text)
  const handleProviderChange = (value) => {
    setForm((prev) => ({ ...prev, provider_name: value, model_name: "" }));
  };

  const currentProviderModels = groupedModels[form.provider_name] || [];

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    if (!form.description.trim())
      return Swal.fire("Validation", "Description is required.", "warning");
    if (!form.provider_name.trim())
      return Swal.fire("Validation", "Provider is required.", "warning");
    if (!form.model_name.trim())
      return Swal.fire("Validation", "Model is required.", "warning");
    if (!isEditing && !form.api_key.trim())
      return Swal.fire("Validation", "API key is required.", "warning");
    return true;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (validateForm() !== true) return;
    setLoading(true);

    const payload = {
      description: form.description.trim(),
      provider_name: form.provider_name.trim(),
      model_name: form.model_name.trim(),
      is_active: form.is_active ? 1 : 0,
    };
    if (form.api_key.trim()) payload.api_key = form.api_key.trim();

    try {
      let res;
      if (isEditing) {
        res = await axios.put(
          `${configure.nodeApi}/aiCredentials/${editId}`,
          payload,
          { headers: { Accept: "application/json" } },
        );
      } else {
        res = await axios.post(`${configure.nodeApi}/aiCredentials`, payload, {
          headers: { Accept: "application/json" },
        });
      }
      if (res.data.success) {
        Swal.fire({
          icon: "success",
          title: isEditing ? "Updated!" : "Saved!",
          text: `AI key ${isEditing ? "updated" : "added"} successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });
        closeModal();
        fetchCredentials();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Other actions ──────────────────────────────────────────────────────────
  const handleSetActive = async (cred) => {
    if (cred.is_active) return;

    Swal.fire({
    icon: "question",
    title: "Set as active key?",
    text: `"${cred.description}" will become the active AI key. Any other active key will be deactivated.`,
    showCancelButton: true,
    confirmButtonText: "Yes, set active",
    confirmButtonColor: "#981f77",
  }).then(async (result) => {
    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const res = await axios.post(
        `${configure.nodeApi}/aiCredentials/${cred.id}/set-active`,
        {},
        { headers: { Accept: "application/json" } },
      );
      if (res.data.success) {
        Swal.fire({
          icon: "success",
          text: `"${cred.description}" is now the active AI key.`,
          timer: 1200,
          showConfirmButton: false,
        });
        fetchCredentials();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  });
};

  const handleDelete = (cred) => {
    if (cred.is_active) {
      Swal.fire(
        "Can't delete",
        "This is the active key. Set another key as active first.",
        "warning",
      );
      return;
    }
    Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: `Delete "${cred.description}"? This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      confirmButtonColor: "#dc2626",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      setLoading(true);
      try {
        const res = await axios.delete(
          `${configure.nodeApi}/aiCredentials/${cred.id}`,
          { headers: { Accept: "application/json" } },
        );
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            text: "Deleted successfully.",
            timer: 1200,
            showConfirmButton: false,
          });
          fetchCredentials();
        }
      } catch (err) {
        Swal.fire("Error", "Could not delete credential.", "error");
      } finally {
        setLoading(false);
      }
    });
  };

  const filteredCredentials = credentials.filter((c) =>
    c.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ── Shared input style ─────────────────────────────────────────────────────
  const inputStyle = {
    fontSize: 13,
    borderRadius: 7,
    border: "1px solid #e5e7eb",
    padding: "8px 12px",
    color: "#1f2937",
    background: "#fff",
    height: "auto",
    lineHeight: 1.5,
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {loading && <Loader />}

      {/* ── Page panel ── */}
      <div className="p-4 bg-white shadow">
        {/* Header row */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 24,
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h5
              style={{
                fontWeight: 700,
                color: "#1f2937",
                marginBottom: 4,
                fontSize: 17,
              }}
            >
              Manage AI Key
            </h5>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {/* Search */}
            <div className="search-box d-inline-block">
              <div className="position-relative">
                <label htmlFor="ai-key-search" className="search-label">
                  <input
                    id="ai-key-search"
                    type="text"
                    className="form-control rounded"
                    placeholder="Search AI keys..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ fontSize: 13, minWidth: 200 }}
                  />
                </label>
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </div>

            <button
              className="btn btn-purple btn-label"
              onClick={openAddModal}
              style={{ whiteSpace: "nowrap" }}
            >
              <i className="fas fa-plus me-2 icon-size label-icon"></i>
              Add AI Key
            </button>
          </div>
        </div>

        {/* Stats row */}
        {credentials.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 20,
              flexWrap: "wrap",
            }}
          >
            {[
              {
                label: "Total",
                value: credentials.length,
                color: "#6b7280",
                bg: "#f3f4f6",
              },
              {
                label: "Active",
                value: credentials.filter((c) => c.is_active).length,
                color: "#166534",
                bg: "#dcfce7",
              },
              {
                label: "Inactive",
                value: credentials.filter((c) => !c.is_active).length,
                color: "#6b7280",
                bg: "#f1f5f9",
              },
            ].map((stat) => (
              <div
                key={stat.label}
                style={{
                  padding: "6px 14px",
                  borderRadius: 20,
                  background: stat.bg,
                  fontSize: 12,
                  fontWeight: 600,
                  color: stat.color,
                }}
              >
                {stat.value} {stat.label}
              </div>
            ))}
          </div>
        )}

        {/* Credential list */}
        {filteredCredentials.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#9ca3af",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "#f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <i
                className="fas fa-key"
                style={{ fontSize: 24, color: "#d1d5db" }}
              ></i>
            </div>
            <p style={{ fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>
              No AI keys found
            </p>
            <p style={{ fontSize: 12, margin: 0 }}>
              {searchTerm
                ? "Try a different search term."
                : 'Click "Add AI Key" to get started.'}
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filteredCredentials.map((cred) => (
              <CredentialCard
                key={cred.id}
                cred={cred}
                onEdit={() => openEditModal(cred)}
                onDelete={() => handleDelete(cred)}
                onSetActive={() => handleSetActive(cred)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Add / Edit Modal ── */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="lg" centered>
        {/* Modal header */}
        <div
          style={{
            padding: "20px 24px 16px",
            borderBottom: "1px solid #f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: "#f5e8f1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-key"
                style={{ color: "#981f77", fontSize: 15 }}
              ></i>
            </div>
            <div>
              <h5
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#1f2937",
                  margin: 0,
                }}
              >
                {isEditing ? "Edit AI Key" : "Add AI Key"}
              </h5>
              <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>
                {isEditing
                  ? "Update this AI provider credential"
                  : "Connect a new AI provider API key"}
              </p>
            </div>
          </div>
          <button
            onClick={closeModal}
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              border: "1px solid #e5e7eb",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9ca3af",
              fontSize: 16,
            }}
          >
            &times;
          </button>
        </div>

        {/* Modal body */}
        <div style={{ padding: "24px", maxHeight: "75vh", overflowY: "auto" }}>
          <Form onSubmit={handleSave} autoComplete="off">
            {/* Description */}
            <div style={{ marginBottom: 20 }}>
              <FieldLabel required>Description</FieldLabel>
              <Input
                type="text"
                placeholder="e.g. Production Claude Key, Fallback OpenAI Key"
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                style={inputStyle}
                autoComplete="off"
              />
              <FieldHint>A friendly label to identify this AI key.</FieldHint>
            </div>

            {/* Provider & Model */}
            <Divider label="Provider & Model" />

            <Row style={{ marginBottom: 16 }}>
              <Col md={5} style={{ paddingRight: 8 }}>
                <FieldLabel required>Provider</FieldLabel>
                <Input
                  type="select"
                  value={form.provider_name}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  style={inputStyle}
                >
                  {PROVIDER_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={7} style={{ paddingLeft: 8 }}>
                <FieldLabel required>Model</FieldLabel>
                {form.provider_name === "Other" ? (
                  <Input
                    type="text"
                    placeholder="e.g. mistralai/mixtral-8x7b"
                    value={form.model_name}
                    onChange={(e) => updateField("model_name", e.target.value)}
                    style={inputStyle}
                  />
                ) : (
                  <Input
                    type="select"
                    value={form.model_name}
                    onChange={(e) => updateField("model_name", e.target.value)}
                    style={inputStyle}
                    disabled={loadingModels}
                  >
                    <option value="">
                      {loadingModels
                        ? "Loading models…"
                        : "— Select a model —"}
                    </option>
                    {currentProviderModels.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name || m.id}
                      </option>
                    ))}
                  </Input>
                )}
                <FieldHint>
                  {form.provider_name === "Other"
                    ? "Enter the full OpenRouter model id."
                    : "Pulled live from OpenRouter's model catalog."}
                </FieldHint>
              </Col>
            </Row>

            {/* API Key */}
            <Divider label="Credentials" />

            <div style={{ marginBottom: 4 }}>
              <FieldLabel required={!isEditing}>
                API Key{" "}
                {isEditing && (
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (blank = keep existing)
                  </span>
                )}
              </FieldLabel>
              <div style={{ position: "relative" }}>
                <Input
                  type={showKey ? "text" : "password"}
                  placeholder={
                    isEditing ? "Enter a new key to replace it" : "sk-..."
                  }
                  value={form.api_key}
                  onChange={(e) => updateField("api_key", e.target.value)}
                  style={{ ...inputStyle, paddingRight: 40 }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((p) => !p)}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#9ca3af",
                    padding: 0,
                    fontSize: 13,
                  }}
                >
                  <i className={`fas ${showKey ? "fa-eye-slash" : "fa-eye"}`}></i>
                </button>
              </div>
              <FieldHint>
                <i className="fas fa-lock me-1" style={{ fontSize: 10 }}></i>
                Stored server-side, masked whenever it's displayed.
              </FieldHint>
            </div>

            {/* Options */}
            <Divider label="Options" />

            <Toggle
              id="is_active"
              checked={form.is_active}
              onChange={(val) => updateField("is_active", val)}
              label="Set as Active"
              hint="Deactivates any other key currently in use"
            />

            {/* Footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 24,
                paddingTop: 20,
                borderTop: "1px solid #f3f4f6",
              }}
            >
              <button
                type="button"
                onClick={closeModal}
                className="btn btn-red btn-label"
              >
                <i className="dripicons-cross me-2 icon-center label-icon"></i>
                Cancel
              </button>
              <button type="submit" className="btn btn-save btn-label">
                <i className="fas fa-save me-2 label-icon"></i>
                {isEditing ? "Update" : "Save Key"}
              </button>
            </div>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default ManageAiKeyIndex;